import {ChangeDetectionStrategy, Component, computed} from "@angular/core"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, SelectionPossibilities} from "@template-editor/components/value-slot/value-slot.component"
import {findClosestParentComponentGraph, collectMattressNodes, isMattressCurveLikeNode, isMattressReferenceFrameLikeNode} from "@cm/mattress-nodes"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode, MattressComputedCurve, MattressComputedCurveParameters} from "@cm/mattress-nodes"

@Component({
    selector: "cm-mattress-computed-curve-inspector",
    templateUrl: "./mattress-computed-curve-inspector.component.html",
    styleUrl: "./mattress-computed-curve-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MattressComputedCurveInspectorComponent extends BaseInspectorComponent<MattressComputedCurve> {
    readonly typeSelectionPossibilities: SelectionPossibilities<MattressComputedCurveParameters["parameters"]["type"]> = [
        {name: "Swap Reference Frame", value: "MattressComputedCurveSwapReferenceFrame"},
    ]

    changeType($event: unknown) {
        if (typeof $event !== "string") return
        const type = $event as MattressComputedCurveParameters["parameters"]["type"]

        let newParameters: MattressComputedCurveParameters["parameters"]
        if (type === "MattressComputedCurveSwapReferenceFrame") {
            newParameters = {type: "MattressComputedCurveSwapReferenceFrame", curve: null, oldReferenceFrame: null, newReferenceFrame: null}
        } else {
            return
        }

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({parameters: newParameters})
        })
    }

    readonly $availableCurves = computed(() => {
        const node = this.$node()
        const parentGraph = findClosestParentComponentGraph(node)
        return Array.from(collectMattressNodes(parentGraph, isMattressCurveLikeNode)).filter((curve) => curve !== node)
    })

    readonly $curveSelectionPossibilities = computed<SelectionPossibilities<MattressCurveLikeNode | null>>(() => [
        {name: "None", value: null},
        ...this.$availableCurves().map((curve) => ({name: curve.parameters.name, value: curve})),
    ])

    readonly $newReferenceFrameSelectionPossibilities = computed<SelectionPossibilities<MattressReferenceFrameLikeNode | null>>(() => {
        const node = this.$node()
        const parentGraph = findClosestParentComponentGraph(node)
        const referenceFrames = Array.from(collectMattressNodes(parentGraph, isMattressReferenceFrameLikeNode))
        return [{name: "None", value: null}, ...referenceFrames.map((rf) => ({name: rf.parameters.name, value: rf}))]
    })

    readonly $oldReferenceFrameSelectionPossibilities = computed<SelectionPossibilities<MattressReferenceFrameLikeNode | null>>(() => {
        return this.$newReferenceFrameSelectionPossibilities()
    })
}
